/**
 * Pure builders for the parking requests' events in the host activity log.
 * No Angular, no HTTP — unit-testable in isolation. Only ids, the booking's flats
 * (id + name), the parking place's name (a non-personal label such as "P12"), the
 * period's days, the paid flag and field NAMES are stored — never a licence plate
 * (at most whether one was given), its country, a guest's or driver's name, or a price.
 */
import { flatsMetadata } from '@components/pages/activity-log/services/activity-log-flat.util';
import type { IActivityFlat } from '@components/pages/activity-log/services/activity-log-flat.util';
import { idFromIri } from '@components/pages/short-term-rental/shared/host-activity.metadata';
import type { HostActivityEvent } from '@components/pages/short-term-rental/shared/host-activity.metadata';
import type { IFlatModel } from '@services/api/flat/flat-model.interface';
import type { UiSelectItem } from '@services/util/select-item-loader.service';
import { format, isValid } from 'date-fns';
import type { ParkingRequestRow } from '../models/parking-request.model';
import type { CurrentFormValues } from './parking-request-payload-builder.service';

type Metadata = Record<string, unknown>;
/** The booking's flats (a booking can cover several); missing ones are skipped. */
type BookingFlats = readonly (IActivityFlat | null)[];

/** How the log stores a day of the period. */
const DAY_FORMAT = 'yyyy-MM-dd';
/** The paid flag's name in the PATCH body. */
const PAID_FIELD = 'paid';
/** The record id the PATCH body carries next to the changed fields. */
const ID_FIELD = 'id';

/** Where a parking request was deleted. */
export type ParkingDeleteOrigin = 'parking_list' | 'parking_form';

/** What the log needs to know of a parking request (before / after a save). */
export interface ParkingActivitySnapshot {
  readonly bookingId: number | null;
  /** The booking's apartment flats — not the parking place. */
  readonly flats: BookingFlats;
  readonly placeId: number | null;
  readonly placeName: string | null;
  readonly startDate: Date | null;
  readonly endDate: Date | null;
  readonly paid: boolean;
  readonly disabledPlace: boolean;
  readonly hasPlate: boolean;
}

/**
 * The parking request form's values as the log sees them (the licence plate only as "given or not")
 * @param input The form values, the parking place options (for the place's name) and the chosen booking's flats
 * @returns The snapshot
 */
export function parkingFormSnapshotOf(input: {
  values: CurrentFormValues;
  places: readonly UiSelectItem<IFlatModel>[];
  bookingFlats?: BookingFlats;
}): ParkingActivitySnapshot {
  const { values, places, bookingFlats } = input;
  const placeName = places.find((place) => place.value === values.parkingPlaceValue)?.model?.name || null;
  return {
    bookingId: idFromIri(values.bookingValue),
    flats: bookingFlats ?? [],
    placeId: idFromIri(values.parkingPlaceValue),
    placeName,
    startDate: values.startDateValue,
    endDate: values.endDateValue,
    paid: values.paidValue,
    disabledPlace: values.disabledParkingPlaceValue,
    hasPlate: !values.licensePlateNotKnownValue && !!values.licensePlateValue?.trim()
  };
}

/**
 * A parking requests list row as the log sees it (the row's "Unknown" placeholders and 0 ids as null)
 * @param row The list row
 * @returns The snapshot
 */
export function parkingRowSnapshotOf(row: ParkingRequestRow): ParkingActivitySnapshot {
  return {
    bookingId: row.booking?.id || null,
    flats: row.bookingFlats,
    placeId: row.parkingPlaceId || null,
    placeName: row.parkingPlaceId ? row.parkingPlace || null : null,
    startDate: row.startDate,
    endDate: row.endDate,
    paid: row.paid,
    disabledPlace: false,
    hasPlate: !!row.licensePlate
  };
}

/**
 * `parking_request_create`: the new request's booking, place, period and flags
 * @param input The created request's id and the saved form values
 * @returns The event metadata
 */
export function parkingRequestCreateMetadata(input: { parkingRequestId: number | null; after: ParkingActivitySnapshot }): Metadata {
  const { parkingRequestId, after } = input;
  return {
    ...parkingRef(parkingRequestId, after),
    paid: after.paid,
    disabled_parking_place: after.disabledPlace,
    has_plate: after.hasPlate
  };
}

/**
 * The events of one successful parking request PATCH: `parking_paid_change` when the paid
 * flag flipped, and `parking_request_update` with the NAMES of the other sent fields (plus
 * the previous place / booking when those changed). Nothing sent → no event.
 * @param input The request, its state before (null when unknown) and after, and the sent field names
 * @returns The events, possibly empty
 */
export function parkingRequestUpdateEvents(input: {
  parkingRequestId: number;
  before: ParkingActivitySnapshot | null;
  after: ParkingActivitySnapshot;
  fields: readonly string[];
}): HostActivityEvent[] {
  const paid = paidChangeEvent(input);
  const fields = input.fields.filter((field) => field !== ID_FIELD && (paid === null || field !== PAID_FIELD));
  return [paid, fields.length > 0 ? updateEvent({ ...input, fields }) : null].filter((event): event is HostActivityEvent => event !== null);
}

/**
 * `parking_payment_generate`: a payment transaction (payment link) generated for a request
 * @param input The request, its booking (0 or null when unknown) and the booking's flats
 * @returns The event metadata
 */
export function parkingPaymentGenerateMetadata(input: { parkingRequestId: number; bookingId: number | null; flats?: BookingFlats }): Metadata {
  return { parking_request_id: input.parkingRequestId, booking_id: input.bookingId || null, ...flatsMetadata(input.flats ?? []) };
}

/**
 * `parking_request_delete`: what the deleted request was, and where it was deleted
 * @param input The request, its state at the delete, and the screen
 * @returns The event metadata
 */
export function parkingRequestDeleteMetadata(input: {
  parkingRequestId: number;
  before: ParkingActivitySnapshot;
  origin: ParkingDeleteOrigin;
}): Metadata {
  const { parkingRequestId, before, origin } = input;
  return { ...parkingRef(parkingRequestId, before), paid: before.paid, origin };
}

/**
 * A day of the period as stored ("2026-10-14")
 * @param date The day
 * @returns The day text, or null when unknown
 */
export function parkingDayOf(date: Date | null): string | null {
  return date && isValid(date) ? format(date, DAY_FORMAT) : null;
}

function parkingRef(parkingRequestId: number | null, state: ParkingActivitySnapshot, flats: BookingFlats = state.flats): Metadata {
  return {
    parking_request_id: parkingRequestId,
    booking_id: state.bookingId,
    ...flatsMetadata(flats),
    parking_place_id: state.placeId,
    parking_place_name: state.placeName,
    start_date: parkingDayOf(state.startDate),
    end_date: parkingDayOf(state.endDate)
  };
}

function paidChangeEvent(input: {
  parkingRequestId: number;
  before: ParkingActivitySnapshot | null;
  after: ParkingActivitySnapshot;
  fields: readonly string[];
}): HostActivityEvent | null {
  const { parkingRequestId, before, after, fields } = input;
  if (!before || !fields.includes(PAID_FIELD) || before.paid === after.paid) return null;
  const metadata = {
    parking_request_id: parkingRequestId,
    booking_id: after.bookingId,
    ...flatsMetadata(updatedFlatsOf(before, after)),
    paid_from: before.paid,
    paid_to: after.paid
  };
  return { action: 'parking_paid_change', metadata };
}

function updateEvent(input: {
  parkingRequestId: number;
  before: ParkingActivitySnapshot | null;
  after: ParkingActivitySnapshot;
  fields: readonly string[];
}): HostActivityEvent {
  const { parkingRequestId, before, after, fields } = input;
  const placeMoved = before !== null && before.placeId !== after.placeId;
  const rebooked = before !== null && before.bookingId !== after.bookingId;
  const metadata = {
    ...parkingRef(parkingRequestId, after, updatedFlatsOf(before, after)),
    fields: [...fields],
    ...(placeMoved ? { previous_parking_place_id: before.placeId, previous_parking_place_name: before.placeName } : {}),
    ...(rebooked ? { previous_booking_id: before.bookingId } : {})
  };
  return { action: 'parking_request_update', metadata };
}

/** The booking's flats after an update, with those before it (the same booking's, or the former one's when rebooked). */
function updatedFlatsOf(before: ParkingActivitySnapshot | null, after: ParkingActivitySnapshot): BookingFlats {
  return [...after.flats, ...(before?.flats ?? [])];
}
